/**
 * Visual style for the user's own CV (TEC-711): one accent colour for the name and section headings,
 * a sans or serif typeface, and a design (header treatment and density). Kept to a few calm,
 * print-safe presets: a CV that reads well in black and white is still the norm in Finland, so the
 * default is the plain ink the templates use.
 */
export const CV_ACCENTS = [
  { id: 'ink', hex: '1B1E21' },
  { id: 'navy', hex: '1F3A5F' },
  { id: 'forest', hex: '2F5D46' },
  { id: 'burgundy', hex: '7A2E3A' },
  { id: 'teal', hex: '1D5C63' },
] as const;

export type CvAccentId = (typeof CV_ACCENTS)[number]['id'];
export type CvFont = 'sans' | 'serif';

/**
 * classic: name left, rule under each heading (the template page's look). centered: header and
 * headings centred. banded: the header on a band of the accent colour. compact: smaller type and
 * tighter spacing, for fitting a long history on fewer pages. All four stay single-column and
 * plain text, so recruitment systems read them the same.
 */
export const CV_DESIGNS = ['classic', 'centered', 'banded', 'compact'] as const;
export type CvDesign = (typeof CV_DESIGNS)[number];

export interface CvStyle {
  accent: CvAccentId;
  font: CvFont;
  design: CvDesign;
}

export const DEFAULT_CV_STYLE: CvStyle = { accent: 'ink', font: 'sans', design: 'classic' };

export function accentHex(accent: string | undefined): string {
  return (CV_ACCENTS.find((a) => a.id === accent) ?? CV_ACCENTS[0]).hex;
}

export function coerceCvStyle(value: unknown): CvStyle {
  const v = (value && typeof value === 'object' ? value : {}) as Partial<CvStyle>;
  return {
    accent: CV_ACCENTS.some((a) => a.id === v.accent) ? (v.accent as CvAccentId) : 'ink',
    font: v.font === 'serif' ? 'serif' : 'sans',
    design: CV_DESIGNS.includes(v.design as CvDesign) ? (v.design as CvDesign) : 'classic',
  };
}
